import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { resolveSearchHighlight } from '../lib/searchHighlight'
import { useSearchParams, useNavigate } from 'react-router-dom'
import {
  Alert,
  Button,
  Dropdown,
  Form,
  Input,
  Modal,
  Pagination,
  Popover,
  Progress,
  Segmented,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Typography,
  message,
} from 'antd'
import type { MenuProps } from 'antd'
import {
  AppstoreOutlined,
  ControlOutlined,
  DeleteOutlined,
  DownOutlined,
  EditOutlined,
  FileAddOutlined,
  FileExcelOutlined,
  FileImageOutlined,
  FileOutlined,
  FilePdfOutlined,
  FilePptOutlined,
  FileWordOutlined,
  MoreOutlined,
  PartitionOutlined,
  UnorderedListOutlined,
  FolderOpenOutlined,
  UploadOutlined,
} from '@ant-design/icons'
import { KnowledgeAdvancedSearch, type KnowledgeListFilters } from './KnowledgeAdvancedSearch'
import { KnowledgeItemGrid } from './KnowledgeItemGrid'
import { KnowledgeChunksDrawer } from './KnowledgeItemPanels'
import { tagsFromForm } from './knowledgeTags'
import { KnowledgeCategoryBrowseField, KnowledgeTagsBrowseField } from './ui'
import { adminApi, formatApiError, PAGE_SIZE, type PageResult } from '../api'
import { CHUNK_STRATEGY_OPTIONS, type KnowledgeScope, scopeQuery } from '../constants/knowledgeScope'
import { openKnowledgeDocEdit, openKnowledgeDocPreview } from '../lib/knowledgeDocPreview'
import { isOnlineEditableDocument } from './knowledgeFilePreview'

type ChunkStrategyOpt = { value: string; label: string }
type ChunkSettings = {
  default_strategy: string
  max_upload_mb?: number
  folder_max_depth?: number
  strategies: { id: string; label: string; enabled: boolean }[]
}
import { KnowledgeFolderTree } from './KnowledgeFolderTree'
import { folderIdFromKey, isIngestionInboxFolder, type FolderLabel, type TreeKey } from './knowledgeFolderUtils'
import { PageHeader, formatDateOrDash, listPagination, tablePagination, usePagedQuery } from './ui'
import {
  UPLOAD_ACCEPT,
  enqueueKnowledgeUpload,
  fileTaskId,
  isSupportedUploadFile,
  normalizeUploadRelativePath,
  subscribeUploadBatchComplete,
  subscribeUploadFileSuccess,
} from '../services/knowledgeUploadQueue'
import { subscribeKnowledgeDocSaved } from '../services/knowledgeDocSync'
import './KnowledgeManage.css'

type K = {
  id: number
  folder_id: number | null
  title: string
  category: string
  entity_type: string
  tags: string
  source: string
  is_active: boolean
  index_status: string
  chunk_count: number
  quality_tier?: string
  quality_score?: number | null
  quality_source?: string
  redundancy_tier?: string
  chunk_strategy?: string
  files_json?: { original_name?: string; stored_name?: string }[] | null
  relations_json?: { source_entity: string; relation: string; target: string }[] | null
  relations_status?: string
  created_at: string
  updated_at: string
}

function isRelationsExtracted(status?: string) {
  return status === 'pending' || status === 'confirmed'
}

const emptyFilters = (): KnowledgeListFilters => ({ q: '' })

type ListViewMode = 'list' | 'grid'

function readViewMode(scope: KnowledgeScope): ListViewMode {
  try {
    const v = localStorage.getItem(`kb-view-mode-${scope}`)
    return v === 'grid' ? 'grid' : 'list'
  } catch {
    return 'list'
  }
}

const statusColor: Record<string, string> = {
  indexed: 'green',
  pending: 'orange',
  stale: 'gold',
  error: 'red',
  empty: 'default',
}

const qualityLabel: Record<string, string> = {
  high: '高',
  medium: '中',
  low: '低',
  unknown: '未评',
}

const qualityColor: Record<string, string> = {
  high: 'green',
  medium: 'gold',
  low: 'red',
  unknown: 'default',
}

const redundancyLabel: Record<string, string> = {
  none: '无',
  light: '轻度',
  heavy: '高度',
  unknown: '未评',
}

const redundancyColor: Record<string, string> = {
  none: 'green',
  light: 'orange',
  heavy: 'red',
  unknown: 'default',
}

const emptyPage = (p: number): PageResult<K> => ({
  items: [],
  total: 0,
  page: p,
  page_size: PAGE_SIZE,
})

type StagedFile = {
  file: File
  relativePath?: string
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function uploadFileIcon(name: string) {
  const ext = name.split('.').pop()?.toLowerCase() || ''
  if (ext === 'pdf') return <FilePdfOutlined className="kg-upload-file-icon kg-upload-file-icon--pdf" />
  if (ext === 'doc' || ext === 'docx' || ext === 'rtf') {
    return <FileWordOutlined className="kg-upload-file-icon kg-upload-file-icon--word" />
  }
  if (ext === 'xls' || ext === 'xlsx') {
    return <FileExcelOutlined className="kg-upload-file-icon kg-upload-file-icon--excel" />
  }
  if (ext === 'pptx') return <FilePptOutlined className="kg-upload-file-icon kg-upload-file-icon--ppt" />
  if (ext === 'md' || ext === 'markdown') {
    return <FileOutlined className="kg-upload-file-icon kg-upload-file-icon--md" />
  }
  if (['png', 'jpg', 'jpeg', 'webp', 'tif', 'tiff', 'bmp'].includes(ext)) {
    return <FileImageOutlined className="kg-upload-file-icon kg-upload-file-icon--image" />
  }
  return <FileOutlined className="kg-upload-file-icon" />
}

type IndexJobMeta = {
  job_id: string
  job_type: 'batch' | 'build_all'
  status: string
  progress: number
  total: number
  indexed: number
  failed: number
  status_message: string
  error_message?: string | null
}

type Props = {
  knowledgeScope?: KnowledgeScope
  title: string
  description: string
}

export function KnowledgeManage({
  knowledgeScope = 'personal',
  title,
  description,
}: Props) {
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()
  const scopeParams = useMemo(() => scopeQuery(knowledgeScope), [knowledgeScope])
  const [esStatus, setEsStatus] = useState<{
    enabled: boolean
    connected: boolean
    indices?: { enterprise?: string }
  } | null>(null)
  const [folderLabels, setFolderLabels] = useState<FolderLabel[]>([])
  const [folderDimensionId, setFolderDimensionId] = useState<number | undefined>(() => {
    try {
      const raw = localStorage.getItem(`kb-folder-dimension-${knowledgeScope}`)
      const id = raw ? Number(raw) : NaN
      return Number.isFinite(id) && id > 0 ? id : undefined
    } catch {
      return undefined
    }
  })
  const [treeKey, setTreeKey] = useState<TreeKey>('all')
  const [listFilters, setListFilters] = useState<KnowledgeListFilters>(emptyFilters)
  const [viewMode, setViewMode] = useState<ListViewMode>(() => readViewMode(knowledgeScope))
  const [qualityOverrideItem, setQualityOverrideItem] = useState<K | null>(null)
  const [qualityOverrideForm] = Form.useForm()
  const [qualityReassessing, setQualityReassessing] = useState(false)
  const [attrExtracting, setAttrExtracting] = useState(false)
  const [datasetModalOpen, setDatasetModalOpen] = useState(false)
  const [datasetPickId, setDatasetPickId] = useState<number | undefined>()
  const [datasetOptions, setDatasetOptions] = useState<{ value: number; label: string }[]>([])
  const [folderTreeRefreshToken, setFolderTreeRefreshToken] = useState(0)
  const [strategyOptions, setStrategyOptions] =
    useState<ChunkStrategyOpt[]>(() => [...CHUNK_STRATEGY_OPTIONS])
  const [defaultStrategy, setDefaultStrategy] = useState('fixed')
  const [maxUploadMb, setMaxUploadMb] = useState(50)
  const [folderMaxDepth, setFolderMaxDepth] = useState(8)
  const [ocrEnabled, setOcrEnabled] = useState(false)
  const [defaultUseOcr, setDefaultUseOcr] = useState(false)
  const [ocrProviderLabel, setOcrProviderLabel] = useState('')

  useEffect(() => {
    setViewMode(readViewMode(knowledgeScope))
    try {
      const raw = localStorage.getItem(`kb-folder-dimension-${knowledgeScope}`)
      const id = raw ? Number(raw) : NaN
      setFolderDimensionId(Number.isFinite(id) && id > 0 ? id : undefined)
    } catch {
      setFolderDimensionId(undefined)
    }
  }, [knowledgeScope])

  useEffect(() => {
    try {
      localStorage.setItem(`kb-view-mode-${knowledgeScope}`, viewMode)
    } catch {
      /* ignore */
    }
  }, [knowledgeScope, viewMode])

  const loadFolderLabels = useCallback(() => {
    const params: Record<string, unknown> = { ...scopeParams }
    if (folderDimensionId != null) params.dimension_id = folderDimensionId
    return adminApi
      .get<{ items: FolderLabel[] }>('/admin/knowledge/folders/labels', { params })
      .then((r) => setFolderLabels(r.data.items))
      .catch((e) => message.error(e.message))
  }, [scopeParams, folderDimensionId])

  useEffect(() => {
    void loadFolderLabels()
  }, [loadFolderLabels])

  useEffect(() => {
    adminApi
      .get<{ items: { id: number; name: string }[] }>('/admin/datasets/options', {
        params: { ...scopeParams, published_only: false },
      })
      .then((r) =>
        setDatasetOptions((r.data.items || []).map((d) => ({ value: d.id, label: d.name }))),
      )
      .catch(() => setDatasetOptions([]))
  }, [scopeParams])

  const batchAddToDataset = () => {
    if (!datasetPickId || !selectedRowKeys.length) {
      message.warning('请选择数据集和文档')
      return
    }
    adminApi
      .post(`/admin/datasets/${datasetPickId}/items`, {
        item_ids: selectedRowKeys,
        mode: 'include',
      })
      .then((r) => {
        message.success(`已加入 ${r.data.added ?? selectedRowKeys.length} 条`)
        setDatasetModalOpen(false)
      })
      .catch((e) => message.error(formatApiError(e, '加入失败')))
  }

  useEffect(() => {
    adminApi
      .get<ChunkSettings>('/admin/settings/chunk')
      .then((r) => {
        const opts = r.data.strategies
          .filter((s) => s.enabled)
          .map((s) => ({ value: s.id, label: s.label || s.id }))
        if (opts.length) setStrategyOptions(opts)
        setDefaultStrategy(r.data.default_strategy || 'fixed')
        setMaxUploadMb(r.data.max_upload_mb ?? 50)
        setFolderMaxDepth(r.data.folder_max_depth ?? 8)
      })
      .catch(() => {})
  }, [])

  useEffect(() => {
    adminApi
      .get<{ enabled: boolean; connected: boolean; indices?: { enterprise?: string } }>(
        '/admin/knowledge/search-index/status',
      )
      .then((r) => setEsStatus(r.data))
      .catch(() => setEsStatus({ enabled: false, connected: false }))
  }, [])

  const folderNameMap = useMemo(() => {
    const m = new Map<number, string>()
    for (const f of folderLabels) m.set(f.id, f.name)
    return m
  }, [folderLabels])

  const loadPage = useCallback(
    (p: number, ps: number, ...extra: unknown[]) => {
      const params: Record<string, unknown> = { page: p, page_size: ps }
      const filters =
        extra[0] && typeof extra[0] === 'object'
          ? (extra[0] as KnowledgeListFilters)
          : listFilters
      const q = filters.q.trim()
      const globalTitleSearch = q.length > 0
      if (!globalTitleSearch && treeKey !== 'all') {
        const fid = folderIdFromKey(treeKey)
        if (fid != null) {
          params.folder_id = fid
          params.include_descendants = true
          if (folderDimensionId != null) params.dimension_id = folderDimensionId
        }
      } else if (folderDimensionId != null && (treeKey === 'all' || globalTitleSearch)) {
        params.dimension_id = folderDimensionId
      }
      if (q) params.q = q
      if (filters.quality_tier) params.quality_tier = filters.quality_tier
      if (filters.redundancy_tier) params.redundancy_tier = filters.redundancy_tier
      if (filters.relations_status) params.relations_status = filters.relations_status
      return adminApi
        .get<PageResult<K>>('/admin/knowledge/items', { params: { ...params, ...scopeParams } })
        .then((r) => r.data)
        .catch((e) => {
          message.error(e.message)
          return emptyPage(p)
        })
    },
    [treeKey, scopeParams, listFilters, folderDimensionId],
  )

  const {
    page,
    setPage,
    pageSize,
    total,
    items: rows,
    loading,
    reload,
    handlePaginationChange,
    paginationOptions,
  } = usePagedQuery(loadPage)

  const [uploadRefreshFolderIds, setUploadRefreshFolderIds] = useState<number[]>([])
  const pendingTreeRefreshIdsRef = useRef(new Set<number>())
  const treeRefreshTimerRef = useRef<number | null>(null)
  const listReloadTimerRef = useRef<number | null>(null)

  const treeRefreshFolderIds = useMemo(() => {
    const ids = new Set<number>(uploadRefreshFolderIds)
    const fid = folderIdFromKey(treeKey)
    if (fid != null) ids.add(fid)
    return [...ids]
  }, [uploadRefreshFolderIds, treeKey])

  const flushTreeRefresh = useCallback(() => {
    const ids = [...pendingTreeRefreshIdsRef.current]
    pendingTreeRefreshIdsRef.current.clear()
    if (!ids.length) return
    setUploadRefreshFolderIds(ids)
    setFolderTreeRefreshToken((v) => v + 1)
    void loadFolderLabels()
  }, [loadFolderLabels])

  const scheduleTreeRefresh = useCallback(
    (folderIds: number[]) => {
      for (const id of folderIds) {
        if (Number.isFinite(id)) pendingTreeRefreshIdsRef.current.add(id)
      }
      if (treeRefreshTimerRef.current) window.clearTimeout(treeRefreshTimerRef.current)
      treeRefreshTimerRef.current = window.setTimeout(() => {
        treeRefreshTimerRef.current = null
        flushTreeRefresh()
      }, 600)
    },
    [flushTreeRefresh],
  )

  const scheduleListReload = useCallback(() => {
    if (listReloadTimerRef.current) window.clearTimeout(listReloadTimerRef.current)
    listReloadTimerRef.current = window.setTimeout(() => {
      listReloadTimerRef.current = null
      void reload(page)
    }, 600)
  }, [reload, page])

  const notifyFolderStructureChange = useCallback(() => {
    void loadFolderLabels()
    void reload(page)
    setFolderTreeRefreshToken((v) => v + 1)
  }, [loadFolderLabels, reload, page])

  useEffect(() => {
    return () => {
      if (treeRefreshTimerRef.current) window.clearTimeout(treeRefreshTimerRef.current)
      if (listReloadTimerRef.current) window.clearTimeout(listReloadTimerRef.current)
    }
  }, [])

  useEffect(() => {
    return subscribeUploadFileSuccess((meta) => {
      if (meta.scope !== knowledgeScope) return
      const path = meta.refreshFolderIds.filter((id) => Number.isFinite(id))
      const parents = path.length > 1 ? path.slice(0, -1) : path
      scheduleTreeRefresh(parents)
      scheduleListReload()
      if (meta.indexJobId) setIndexJobId(meta.indexJobId)
    })
  }, [knowledgeScope, scheduleTreeRefresh, scheduleListReload])

  useEffect(() => {
    return subscribeUploadBatchComplete((meta) => {
      if (meta.scope !== knowledgeScope) return
      if (treeRefreshTimerRef.current) {
        window.clearTimeout(treeRefreshTimerRef.current)
        treeRefreshTimerRef.current = null
      }
      pendingTreeRefreshIdsRef.current.add(meta.baseFolderId)
      flushTreeRefresh()
      if (listReloadTimerRef.current) {
        window.clearTimeout(listReloadTimerRef.current)
        listReloadTimerRef.current = null
      }
      void reload(page)
    })
  }, [knowledgeScope, flushTreeRefresh, reload, page])

  useEffect(() => {
    if (treeKey === 'all') return
    const fid = folderIdFromKey(treeKey)
    if (fid == null) return
    if (!folderLabels.some((f) => f.id === fid)) {
      setTreeKey('all')
      setPage(1)
    }
  }, [folderLabels, treeKey, setPage])

  const [uploadOpen, setUploadOpen] = useState(false)
  const openItemEdit = useCallback(
    (itemId: number, files?: K['files_json']) => {
      if (!isOnlineEditableDocument(files)) {
        message.warning('该文档为上传附件（Word/PDF 等），不支持在线编辑正文，请使用预览查看')
        return
      }
      openKnowledgeDocEdit(itemId, knowledgeScope, {
        onNeedLogin: () => {
          message.warning('编辑文档需使用管理账号登录（与后台同一账号）')
          const redirect = `${window.location.pathname}${window.location.search}`
          void navigate(`/manage/login?redirect=${encodeURIComponent(redirect)}`)
        },
      })
    },
    [knowledgeScope, navigate],
  )

  const openItemPreview = useCallback(
    (itemId: number) => {
      openKnowledgeDocPreview(itemId, knowledgeScope, {
        onNeedLogin: () => {
          message.warning('预览文档需使用管理账号登录（与后台同一账号）')
          const redirect = `${window.location.pathname}${window.location.search}`
          void navigate(`/manage/login?redirect=${encodeURIComponent(redirect)}`)
        },
      })
    },
    [knowledgeScope, navigate],
  )

  useEffect(() => {
    const raw = searchParams.get('item')
    if (!raw) return
    const id = Number(raw)
    if (!Number.isFinite(id) || id <= 0) return
    const next = new URLSearchParams(searchParams)
    next.delete('item')
    setSearchParams(next, { replace: true })
    openItemPreview(id)
  }, [searchParams, setSearchParams, openItemPreview])

  const [chunksItem, setChunksItem] = useState<{ id: number; title: string; chunk_count: number } | null>(
    null,
  )
  const [uploadForm] = Form.useForm()
  useEffect(() => {
    adminApi
      .get<{
        enabled: boolean
        default_on_upload: boolean
        provider_label?: string
      }>('/admin/knowledge/ocr-defaults')
      .then((r) => {
        setOcrEnabled(r.data.enabled)
        setDefaultUseOcr(r.data.default_on_upload)
        setOcrProviderLabel(r.data.provider_label || '')
        uploadForm.setFieldValue('use_ocr', r.data.enabled && r.data.default_on_upload)
      })
      .catch(() => {})
  }, [uploadForm])
  const [fileList, setFileList] = useState<StagedFile[]>([])
  const fileInputRef = useRef<HTMLInputElement>(null)
  const folderInputRef = useRef<HTMLInputElement>(null)
  const [indexJobId, setIndexJobId] = useState<string | null>(null)
  const [indexJob, setIndexJob] = useState<IndexJobMeta | null>(null)
  const [indexJobSubmitting, setIndexJobSubmitting] = useState(false)
  const [selectedRowKeys, setSelectedRowKeys] = useState<number[]>([])
  const [batchDeleting, setBatchDeleting] = useState(false)

  const handleFolderDimensionChange = useCallback(
    (id: number) => {
      setFolderDimensionId(id)
      setTreeKey('all')
      setPage(1)
      setSelectedRowKeys([])
      try {
        localStorage.setItem(`kb-folder-dimension-${knowledgeScope}`, String(id))
      } catch {
        /* ignore */
      }
    },
    [knowledgeScope, setPage],
  )

  const currentFolderId = useMemo(() => folderIdFromKey(treeKey), [treeKey])

  const currentFolderMeta = useMemo(() => {
    if (currentFolderId == null) return undefined
    return folderLabels.find((f) => f.id === currentFolderId)
  }, [currentFolderId, folderLabels])

  const isIngestionInbox = useMemo(
    () => (currentFolderMeta ? isIngestionInboxFolder(currentFolderMeta) : false),
    [currentFolderMeta],
  )

  const isAllKnowledge = treeKey === 'all'
  const canUpload = !isAllKnowledge && currentFolderId != null && !isIngestionInbox

  const openCreateDocPage = useCallback(
    (docType: 'markdown' | 'richtext') => {
      if (currentFolderId == null) {
        message.warning('请先在左侧选择文件夹')
        return
      }
      if (isIngestionInbox) {
        message.warning('「采集文件夹」为系统暂存目录，请通过「文件采集」入库')
        return
      }
      const basePath = knowledgeScope === 'enterprise' ? '/enterprise/create' : '/knowledge/create'
      const qs = new URLSearchParams({
        type: docType,
        folder_id: String(currentFolderId),
      })
      if (folderDimensionId != null) qs.set('dimension_id', String(folderDimensionId))
      window.open(`${basePath}?${qs}`, '_blank', 'noopener,noreferrer')
    },
    [currentFolderId, folderDimensionId, knowledgeScope, isIngestionInbox],
  )

  const handleDocSaved = useCallback(
    (meta: { scope: string; folderId: number; indexJobId?: string }) => {
      if (meta.scope !== knowledgeScope) return

      if (Number.isFinite(meta.folderId) && meta.folderId > 0) {
        scheduleTreeRefresh([meta.folderId])
      }
      void reload(page)

      if (meta.indexJobId) {
        setIndexJobId(meta.indexJobId)
      }
    },
    [knowledgeScope, scheduleTreeRefresh, reload, page],
  )

  useEffect(() => subscribeKnowledgeDocSaved(handleDocSaved), [handleDocSaved])

  const onTreeKeyChange = (key: TreeKey) => {
    setTreeKey(key)
    setPage(1)
    setSelectedRowKeys([])
  }

  const indexJobRunning =
    indexJob?.status === 'pending' || indexJob?.status === 'running'

  const runBatchDelete = (ids: number[]) => {
    setBatchDeleting(true)
    const hide = message.loading('正在删除…', 0)
    return adminApi
      .post<{ deleted: number }>(
        '/admin/knowledge/items/batch-delete',
        { ids, delete_all: false },
        { params: scopeParams },
      )
      .then((r) => {
        message.success(`已删除 ${r.data.deleted} 条`)
        setSelectedRowKeys([])
        return reload(page)
      })
      .catch((e) => message.error(formatApiError(e, '删除失败')))
      .finally(() => {
        hide()
        setBatchDeleting(false)
      })
  }

  const confirmBatchDeleteSelected = () => {
    if (!selectedRowKeys.length) {
      message.warning('请先勾选要删除的条目')
      return
    }
    Modal.confirm({
      title: `确认删除选中的 ${selectedRowKeys.length} 条？`,
      content: '将同时删除附件与 Elasticsearch 切片，且不可恢复。',
      okText: '删除',
      okType: 'danger',
      cancelText: '取消',
      onOk: () => runBatchDelete(selectedRowKeys),
    })
  }

  const openQualityOverride = (row: K) => {
    setQualityOverrideItem(row)
    qualityOverrideForm.setFieldsValue({
      quality_tier: row.quality_tier && row.quality_tier !== 'unknown' ? row.quality_tier : undefined,
      redundancy_tier:
        row.redundancy_tier && row.redundancy_tier !== 'unknown' ? row.redundancy_tier : undefined,
      reason: '',
    })
  }

  const submitQualityOverride = () => {
    if (!qualityOverrideItem) return
    void qualityOverrideForm.validateFields().then((v) => {
      adminApi
        .post(`/admin/knowledge/items/${qualityOverrideItem.id}/quality/override`, v, {
          params: scopeParams,
        })
        .then(() => {
          message.success('标签已更新')
          setQualityOverrideItem(null)
          return reload(page)
        })
        .catch((e) => message.error(formatApiError(e, '更新失败')))
    })
  }

  const batchExtractAttributes = () => {
    if (!selectedRowKeys.length) {
      message.warning('请先勾选要抽取的条目')
      return
    }
    setAttrExtracting(true)
    adminApi
      .post<{ failed: number; skipped: number; queued: number }>(
        '/admin/knowledge/key-values/batch-extract',
        { ids: selectedRowKeys },
        { params: scopeParams },
      )
      .then((r) => {
        const { failed, skipped, queued } = r.data
        if (queued === 0) {
          message.info(
            failed || skipped
              ? `未加入队列：失败 ${failed}，跳过 ${skipped}（需已索引）`
              : '没有可抽取的条目',
          )
          return
        }
        message.success(`已加入后台属性抽取队列 ${queued} 条`)
      })
      .catch((e) => message.error(formatApiError(e, '抽取失败')))
      .finally(() => setAttrExtracting(false))
  }

  const reassessSelectedQuality = () => {
    if (!selectedRowKeys.length) {
      message.warning('请先勾选要测评的条目')
      return
    }
    setQualityReassessing(true)
    adminApi
      .post('/admin/knowledge/quality/reassess', { ids: selectedRowKeys }, { params: scopeParams })
      .then((r) => {
        const { assessed, failed } = r.data as { assessed: number; failed: number }
        if (failed > 0) {
          message.warning(
            `测评完成：成功 ${assessed}，失败 ${failed}（未索引的文档需先完成索引）`,
            6,
          )
        } else {
          message.success(`测评完成：成功 ${assessed}`)
        }
        setSelectedRowKeys([])
        return reload(page)
      })
      .catch((e) => message.error(formatApiError(e, '测评失败')))
      .finally(() => setQualityReassessing(false))
  }

  const reindex = useCallback(
    (id: number) => {
      adminApi
        .post<IndexJobMeta>(`/admin/knowledge/items/${id}/index`, null, { params: scopeParams })
        .then((r) => {
          setIndexJobId(r.data.job_id)
          setIndexJob(r.data)
          message.info('索引已在后台开始')
          void reload(page)
        })
        .catch((e) => message.error(formatApiError(e, '启动索引失败'), 8))
    },
    [scopeParams, page, reload],
  )

  const confirmDeleteRow = (id: number) => {
    Modal.confirm({
      title: '确认删除？',
      okText: '删除',
      okType: 'danger',
      cancelText: '取消',
      onOk: () =>
        adminApi
          .delete(`/admin/knowledge/items/${id}`, { params: scopeParams })
          .then(() => {
            message.success('已删')
            setSelectedRowKeys((prev) => prev.filter((k) => k !== id))
            void reload(page)
          }),
    })
  }

  const tableColumns = useMemo(
    () => [
      {
        key: 'id',
        title: 'id',
        dataIndex: 'id',
        width: 72,
      },
      {
        key: 'title',
        title: '标题',
        dataIndex: 'title',
        ellipsis: { showTitle: true },
        width: 280,
        render: (t: string, r: K) => {
          const fileName = r.files_json?.[0]?.original_name || t
          const q = listFilters.q.trim()
          const titleHtml = q ? resolveSearchHighlight(t, q) : null
          return (
            <Typography.Link
              className="knowledge-item-title-link"
              onClick={() => openItemPreview(r.id)}
              title={fileName}
            >
              {titleHtml ? (
                <span dangerouslySetInnerHTML={{ __html: titleHtml }} />
              ) : (
                fileName
              )}
            </Typography.Link>
          )
        },
      },
      {
        key: 'folder',
        title: '文件夹',
        width: 100,
        ellipsis: true,
        render: (_: unknown, r: K) =>
          r.folder_id ? (
            <span className="knowledge-table-cell-text">
              {folderNameMap.get(r.folder_id) || '未命名文件夹'}
            </span>
          ) : (
            <Tag className="knowledge-table-tag">未分类</Tag>
          ),
      },
      { key: 'category', title: '分类', dataIndex: 'category', width: 80, ellipsis: { showTitle: true } },
      {
        key: 'chunk_strategy',
        title: '切片方式',
        dataIndex: 'chunk_strategy',
        width: 120,
        ellipsis: { showTitle: true },
        render: (v: string) => {
          const label = strategyOptions.find((o) => o.value === v)?.label || v || 'fixed'
          return <span className="knowledge-table-cell-text">{label}</span>
        },
      },
      {
        key: 'index',
        title: '索引',
        width: 90,
        render: (_: unknown, r: K) => (
          <Tag className="knowledge-table-tag" color={statusColor[r.index_status] || 'default'}>
            {r.index_status}
          </Tag>
        ),
      },
      { key: 'chunk_count', title: '切片数', dataIndex: 'chunk_count', width: 64 },
      {
        key: 'relations_status',
        title: '图谱抽取',
        width: 88,
        render: (_: unknown, r: K) => {
          const extracted = isRelationsExtracted(r.relations_status)
          return (
            <Tag className="knowledge-table-tag" color={extracted ? 'blue' : 'default'}>
              {extracted ? '已抽取' : '未抽取'}
            </Tag>
          )
        },
      },
      {
        key: 'quality',
        title: '质量',
        width: 88,
        render: (_: unknown, r: K) => {
          const label = `${qualityLabel[r.quality_tier || 'unknown'] || r.quality_tier}${
            r.quality_score != null ? ` ${r.quality_score}` : ''
          }`
          return (
            <Tag
              className="knowledge-table-tag"
              color={qualityColor[r.quality_tier || 'unknown'] || 'default'}
              title={label}
            >
              {label}
            </Tag>
          )
        },
      },
      {
        key: 'redundancy',
        title: '冗余',
        width: 72,
        render: (_: unknown, r: K) => (
          <Tag
            className="knowledge-table-tag"
            color={redundancyColor[r.redundancy_tier || 'unknown'] || 'default'}
          >
            {redundancyLabel[r.redundancy_tier || 'unknown'] || r.redundancy_tier}
          </Tag>
        ),
      },
      {
        key: 'created_at',
        title: '创建时间',
        dataIndex: 'created_at',
        width: 160,
        render: (v: string) => (
          <span className="knowledge-table-cell-text">{formatDateOrDash(v)}</span>
        ),
      },
      {
        key: 'updated_at',
        title: '更新时间',
        dataIndex: 'updated_at',
        width: 160,
        render: (v: string) => (
          <span className="knowledge-table-cell-text">{formatDateOrDash(v)}</span>
        ),
      },
      {
        key: 'actions',
        title: '操作',
        width: 108,
        fixed: 'right' as const,
        className: 'knowledge-table-actions-col',
        render: (_: unknown, r: K) => {
          const menuItems: MenuProps['items'] = [
            {
              key: 'chunks',
              icon: <PartitionOutlined />,
              label: '查看切片',
              disabled: !r.chunk_count,
              onClick: () =>
                setChunksItem({ id: r.id, title: r.title, chunk_count: r.chunk_count }),
            },
            {
              key: 'index',
              label: '索引',
              onClick: () => {
                void reindex(r.id)
              },
            },
            {
              key: 'quality',
              label: '质量',
              onClick: () => openQualityOverride(r),
            },
            { type: 'divider' as const },
            {
              key: 'delete',
              icon: <DeleteOutlined />,
              label: '删除',
              danger: true,
              onClick: () => {
                Modal.confirm({
                  title: '确认删除？',
                  okText: '删除',
                  okButtonProps: { danger: true },
                  cancelText: '取消',
                  onOk: () =>
                    adminApi
                      .delete(`/admin/knowledge/items/${r.id}`, { params: scopeParams })
                      .then(() => {
                        message.success('已删')
                        setSelectedRowKeys((prev) => prev.filter((k) => k !== r.id))
                        void reload(page)
                      }),
                })
              },
            },
          ]
          return (
            <Space size={4} className="knowledge-row-actions">
              <Button size="small" icon={<EditOutlined />} onClick={() => openItemEdit(r.id, r.files_json)}>
                编辑
              </Button>
              <Dropdown menu={{ items: menuItems }} trigger={['click']}>
                <Button size="small" icon={<MoreOutlined />} aria-label="更多操作" />
              </Dropdown>
            </Space>
          )
        },
      },
    ],
    [folderNameMap, strategyOptions, scopeParams, page, reload, reindex, listFilters.q, openItemEdit],
  )

  const startIndexJob = (path: '/admin/knowledge/index/batch' | '/admin/knowledge/index/build-all', body?: { ids: number[] }) => {
    if (indexJobSubmitting || indexJobRunning) return Promise.resolve()
    setIndexJobSubmitting(true)
    return adminApi
      .post<IndexJobMeta>(path, body ?? null, { params: scopeParams })
      .then((r) => {
        setIndexJobId(r.data.job_id)
        setIndexJob(r.data)
        if (r.data.status === 'completed' && r.data.total === 0) {
          setSelectedRowKeys([])
          message.info(r.data.status_message || '没有待处理条目')
        } else if (r.data.status === 'completed') {
          message.success(r.data.status_message)
          setSelectedRowKeys([])
          void reload(page)
        } else {
          setSelectedRowKeys([])
          message.info(
            r.data.job_type === 'batch'
              ? `批量索引已在后台开始（${r.data.total} 条）`
              : '全量索引已在后台开始',
          )
        }
      })
      .catch((e) => message.error(formatApiError(e, '启动索引任务失败'), 8))
      .finally(() => setIndexJobSubmitting(false))
  }

  const confirmBatchIndexSelected = () => {
    if (!selectedRowKeys.length) {
      message.warning('请先勾选要索引的条目')
      return
    }
    Modal.confirm({
      title: `确认索引选中的 ${selectedRowKeys.length} 条？`,
      content: '将在后台异步重新抽取正文、切片并向量化，可继续浏览或翻页勾选其他文档。',
      okText: '开始索引',
      cancelText: '取消',
      onOk: () => startIndexJob('/admin/knowledge/index/batch', { ids: selectedRowKeys }),
    })
  }

  useEffect(() => {
    adminApi
      .get<{ job: IndexJobMeta | null }>('/admin/knowledge/index/jobs/active', { params: scopeParams })
      .then((r) => {
        const job = r.data.job
        if (job && (job.status === 'pending' || job.status === 'running')) {
          setIndexJobId(job.job_id)
          setIndexJob(job)
        }
      })
      .catch(() => {})
  }, [scopeParams])

  useEffect(() => {
    if (!indexJobId) {
      setIndexJob(null)
      return
    }
    let timer: ReturnType<typeof setTimeout> | undefined
    let cancelled = false

    const tick = () => {
      if (cancelled) return
      adminApi
        .get<IndexJobMeta>(`/admin/knowledge/index/jobs/${indexJobId}`)
        .then((r) => {
          if (cancelled) return
          setIndexJob(r.data)
          if (r.data.status === 'pending' || r.data.status === 'running') {
            void reload(page)
            timer = setTimeout(tick, 2000)
            return
          }
          if (r.data.status === 'completed') {
            message.success(r.data.status_message || '索引任务已完成')
            setIndexJobId(null)
            setIndexJob(null)
            setSelectedRowKeys([])
            void reload(page)
          } else if (r.data.status === 'failed') {
            message.error(r.data.error_message || r.data.status_message || '索引任务失败', 8)
            setIndexJobId(null)
            setIndexJob(null)
          }
        })
        .catch((e) => {
          if (!cancelled) {
            const status = (e as { response?: { status?: number } })?.response?.status
            if (status === 404) {
              setIndexJobId(null)
              setIndexJob(null)
              return
            }
            message.error(formatApiError(e, '获取索引进度失败'))
          }
        })
    }
    tick()

    return () => {
      cancelled = true
      if (timer) clearTimeout(timer)
    }
  }, [indexJobId, reload, page])

  const resetUploadModal = () => {
    setFileList([])
    uploadForm.resetFields()
    uploadForm.setFieldsValue({
      use_ocr: ocrEnabled && defaultUseOcr,
      chunk_strategy: defaultStrategy,
      category: '文档',
    })
  }

  const openUploadPicker = (mode: 'file' | 'folder') => {
    if (currentFolderId == null) {
      message.warning('请先在左侧选择文件夹')
      return
    }
    resetUploadModal()
    setUploadOpen(true)
    window.setTimeout(() => {
      if (mode === 'file') fileInputRef.current?.click()
      else folderInputRef.current?.click()
    }, 0)
  }

  const addStagedFiles = (incoming: StagedFile[]) => {
    if (!incoming.length) return
    const limit = maxUploadMb * 1024 * 1024
    let skippedExt = 0
    let skippedSize = 0
    const next: StagedFile[] = []
    for (const item of incoming) {
      const name = item.relativePath || item.file.name
      if (!isSupportedUploadFile(name)) {
        skippedExt += 1
        continue
      }
      if (item.file.size > limit) {
        skippedSize += 1
        continue
      }
      next.push(item)
    }
    if (skippedExt) message.warning(`已跳过 ${skippedExt} 个不支持的文件类型`)
    if (skippedSize) message.warning(`已跳过 ${skippedSize} 个超过 ${maxUploadMb}MB 的文件`)
    if (!next.length) return

    setFileList((prev) => {
      const merged = [...prev]
      for (const item of next) {
        const key = fileTaskId(item.file, item.relativePath)
        if (merged.some((x) => fileTaskId(x.file, x.relativePath) === key)) continue
        merged.push(item)
      }
      return merged
    })
  }

  const submitUpload = async () => {
    if (currentFolderId == null) {
      message.warning('请先在左侧选择文件夹')
      return
    }
    if (fileList.length === 0) {
      message.warning('请选择至少一个文件或文件夹')
      return
    }
    const seenNames = new Set<string>()
    for (const item of fileList) {
      const nameKey = (item.relativePath || item.file.name).toLowerCase()
      if (seenNames.has(nameKey)) {
        message.error(`所选文件中「${item.relativePath || item.file.name}」重复，请去重后再上传`)
        return
      }
      seenNames.add(nameKey)
    }
    let values: {
      category?: string
      entity_type?: string
      tags?: string[]
      use_ocr?: boolean
      chunk_strategy?: string
    }
    try {
      values = await uploadForm.validateFields()
    } catch {
      return
    }

    const baseFolderName = folderNameMap.get(currentFolderId) || '当前文件夹'
    const count = fileList.length
    enqueueKnowledgeUpload({
      scope: knowledgeScope,
      baseFolderId: currentFolderId,
      baseFolderLabel: baseFolderName,
      files: fileList.map((item) => ({
        file: item.file,
        relativePath: item.relativePath,
      })),
      options: {
        category: values.category || '文档',
        entity_type: values.entity_type,
        tags: tagsFromForm(values.tags),
        use_ocr: values.use_ocr,
        chunk_strategy: values.chunk_strategy || defaultStrategy,
      },
    })

    message.success(`已加入传输队列（${count} 个文件），可在右上角「传输管理」查看进度`)
    setUploadOpen(false)
    resetUploadModal()
  }

  const batchPopoverContent = useMemo(() => {
    const sel = selectedRowKeys.length
    return (
      <Space direction="vertical" size={4} className="knowledge-batch-popover-actions">
        <Button
          size="small"
          disabled={!sel || indexJobSubmitting || indexJobRunning}
          loading={indexJobSubmitting}
          onClick={() => confirmBatchIndexSelected()}
        >
          批量索引文档
        </Button>
        <Button
          size="small"
          danger
          disabled={!sel || batchDeleting}
          loading={batchDeleting}
          onClick={() => confirmBatchDeleteSelected()}
        >
          批量删除文档
        </Button>
        <Button
          size="small"
          disabled={!sel || attrExtracting}
          loading={attrExtracting}
          onClick={() => batchExtractAttributes()}
        >
          批量抽取属性
        </Button>
        <Button
          size="small"
          disabled={!sel || qualityReassessing}
          loading={qualityReassessing}
          onClick={() => reassessSelectedQuality()}
        >
          批量测评文档
        </Button>
        <Button
          size="small"
          disabled={!sel || !datasetOptions.length}
          onClick={() => setDatasetModalOpen(true)}
        >
          加入数据集
        </Button>
      </Space>
    )
  }, [
    selectedRowKeys.length,
    indexJobSubmitting,
    indexJobRunning,
    batchDeleting,
    attrExtracting,
    qualityReassessing,
    datasetOptions.length,
  ])

  const batchActionLoading =
    indexJobSubmitting || batchDeleting || attrExtracting || qualityReassessing

  return (
    <div className="knowledge-manage">
      <PageHeader title={title} description={description} />
      {esStatus && (!esStatus.enabled || !esStatus.connected) && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message="Elasticsearch 未就绪"
          description={
            esStatus.enabled
              ? '已开启 ES_ENABLED，但无法连接集群，无法读取切片数据。请检查 ES_URL 并启动 Elasticsearch 8。'
              : '切片与向量仅存 ES。请在 .env 设置 ES_ENABLED=true 并启动 Elasticsearch 8。'
          }
        />
      )}
      <div className="knowledge-manage-body">
        <aside className="knowledge-manage-aside">
          <KnowledgeFolderTree
            knowledgeScope={knowledgeScope}
            treeKey={treeKey}
            onTreeKeyChange={onTreeKeyChange}
            onStructureChange={notifyFolderStructureChange}
            treeRefreshToken={folderTreeRefreshToken}
            refreshFolderIds={treeRefreshFolderIds}
            maxFolderDepth={folderMaxDepth}
            dimensionId={folderDimensionId}
            onDimensionIdChange={handleFolderDimensionChange}
          />
        </aside>
        <div className="knowledge-manage-main">
          <div className="knowledge-manage-toolbar">
            <Space wrap className="knowledge-manage-toolbar-filters" size={8}>
              {canUpload ? (
                <>
                  <Dropdown
                    menu={{
                      items: [
                        {
                          key: 'richtext',
                          icon: <FileAddOutlined />,
                          label: '网页文档',
                          onClick: () => openCreateDocPage('richtext'),
                        },
                        {
                          key: 'markdown',
                          icon: <FileAddOutlined />,
                          label: 'Markdown 文档',
                          onClick: () => openCreateDocPage('markdown'),
                        },
                      ],
                    }}
                    trigger={['hover']}
                    mouseEnterDelay={0.15}
                  >
                    <Button icon={<FileAddOutlined />}>
                      新建
                      <DownOutlined style={{ fontSize: 10, marginLeft: 4 }} />
                    </Button>
                  </Dropdown>
                  <Dropdown
                    menu={{
                      items: [
                        {
                          key: 'file',
                          icon: <UploadOutlined />,
                          label: '上传文件',
                          onClick: () => openUploadPicker('file'),
                        },
                        {
                          key: 'folder',
                          icon: <FolderOpenOutlined />,
                          label: '上传文件夹',
                          onClick: () => openUploadPicker('folder'),
                        },
                      ],
                    }}
                    trigger={['hover']}
                    mouseEnterDelay={0.15}
                  >
                    <Button type="primary" icon={<UploadOutlined />}>
                      上传
                      <DownOutlined style={{ fontSize: 10, marginLeft: 4 }} />
                    </Button>
                  </Dropdown>
                </>
              ) : isIngestionInbox ? (
                <Typography.Text type="secondary">
                  采集暂存目录，仅由「文件采集」写入；解析完成后会自动归类
                </Typography.Text>
              ) : null}
              <KnowledgeAdvancedSearch
                value={listFilters}
                onChange={setListFilters}
                onSearch={(next) => {
                  const payload = next ?? listFilters
                  setListFilters(payload)
                  setPage(1)
                  void reload(1, pageSize, payload)
                }}
              />
              <Segmented
                value={viewMode}
                onChange={(v) => setViewMode(v as ListViewMode)}
                options={[
                  { value: 'list', icon: <UnorderedListOutlined />, title: '列表视图' },
                  { value: 'grid', icon: <AppstoreOutlined />, title: '平铺视图' },
                ]}
              />
              <Popover
                trigger="hover"
                placement="bottomRight"
                overlayClassName="knowledge-batch-popover"
                content={batchPopoverContent}
                mouseEnterDelay={0.15}
              >
                <Button
                  type="text"
                  className="knowledge-batch-trigger"
                  icon={<ControlOutlined />}
                  loading={batchActionLoading}
                  aria-label="批量操作"
                  title="批量操作"
                />
              </Popover>
            </Space>
          </div>
          {indexJob && indexJobRunning ? (
            <Alert
              type="info"
              showIcon
              className="knowledge-build-all-progress"
              message={indexJob.job_type === 'build_all' ? '全量索引进行中' : '索引任务进行中'}
              description={
                <Space direction="vertical" style={{ width: '100%' }}>
                  <Typography.Text type="secondary">{indexJob.status_message}</Typography.Text>
                  <Progress
                    percent={indexJob.progress}
                    status="active"
                    format={() =>
                      indexJob.status_message?.includes('batch')
                        ? indexJob.status_message
                        : indexJob.total > 0
                          ? `${indexJob.indexed + indexJob.failed}/${indexJob.total}`
                          : `${indexJob.progress}%`
                    }
                  />
                </Space>
              }
            />
          ) : null}
          <div className="knowledge-manage-content">
            {viewMode === 'list' ? (
              <div className="knowledge-manage-content-scroll">
                <Table
                  className="knowledge-manage-table"
                  rowKey="id"
                  size="small"
                  loading={loading}
                  dataSource={rows}
                  columns={tableColumns}
                  tableLayout="fixed"
                  scroll={{ x: 1280 }}
                  rowSelection={{
                    selectedRowKeys,
                    onChange: (keys) => setSelectedRowKeys(keys as number[]),
                    preserveSelectedRowKeys: true,
                  }}
                  pagination={tablePagination(page, total, handlePaginationChange, paginationOptions)}
                />
              </div>
            ) : (
              <div className="knowledge-grid-view">
                <KnowledgeItemGrid
                  rows={rows}
                  loading={loading}
                  selectedRowKeys={selectedRowKeys}
                  onSelectChange={setSelectedRowKeys}
                  folderNameMap={folderNameMap}
                  onOpen={openItemPreview}
                  onEdit={openItemEdit}
                  onViewChunks={(item) =>
                    setChunksItem({ id: item.id, title: item.title, chunk_count: item.chunk_count })
                  }
                  onReindex={reindex}
                  onDelete={confirmDeleteRow}
                  onQuality={(item) => {
                    const row = rows.find((r) => r.id === item.id)
                    if (row) openQualityOverride(row)
                  }}
                />
                <div className="kg-grid-pagination">
                  <Pagination {...listPagination(page, total, handlePaginationChange, paginationOptions)} />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <KnowledgeChunksDrawer
        open={chunksItem != null}
        item={chunksItem}
        scopeParams={scopeParams}
        onClose={() => setChunksItem(null)}
      />

      <Modal
        title={qualityOverrideItem ? `质量标签：${qualityOverrideItem.title}` : '质量标签'}
        open={qualityOverrideItem != null}
        onOk={submitQualityOverride}
        onCancel={() => setQualityOverrideItem(null)}
        okText="保存"
        destroyOnClose
      >
        <Form form={qualityOverrideForm} layout="vertical">
          <Form.Item name="quality_tier" label="质量档位">
            <Select
              allowClear
              placeholder="不修改"
              options={[
                { value: 'high', label: '高质量' },
                { value: 'medium', label: '中质量' },
                { value: 'low', label: '低质量' },
                { value: 'unknown', label: '未评' },
              ]}
            />
          </Form.Item>
          <Form.Item name="redundancy_tier" label="冗余档位">
            <Select
              allowClear
              placeholder="不修改"
              options={[
                { value: 'none', label: '无冗余' },
                { value: 'light', label: '轻度冗余' },
                { value: 'heavy', label: '高度冗余' },
                { value: 'unknown', label: '未评' },
              ]}
            />
          </Form.Item>
          <Form.Item
            name="reason"
            label="覆盖原因"
            rules={[{ required: true, message: '请填写覆盖原因' }]}
          >
            <Input.TextArea rows={3} maxLength={500} placeholder="说明为何手动调整标签" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="上传知识文档"
        open={uploadOpen}
        onOk={() => void submitUpload()}
        onCancel={() => {
          setUploadOpen(false)
          resetUploadModal()
        }}
        width={720}
        destroyOnHidden
        styles={{ body: { maxHeight: 'min(72vh, 640px)', overflowY: 'auto' } }}
        okText="开始上传"
      >
        <Form
          form={uploadForm}
          layout="vertical"
          initialValues={{ use_ocr: defaultUseOcr, chunk_strategy: defaultStrategy }}
        >
          <Form.Item
            label="文件"
            extra={`支持 PDF、Word（.doc/.docx/.rtf）、Excel（.xls/.xlsx）、PPT（.pptx）、Markdown（.md/.markdown）、常见图片；单文件 ≤ ${maxUploadMb}MB。可选择多个文件或整个文件夹；文件夹会按目录结构自动创建子文件夹。大批量上传将在后台异步进行，可在右上角「传输管理」查看进度。`}
          >
            <div className="kg-upload-hidden-inputs" aria-hidden="true">
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept={UPLOAD_ACCEPT}
                tabIndex={-1}
                onChange={(e) => {
                  const picked = Array.from(e.target.files || [])
                  addStagedFiles(picked.map((file) => ({ file })))
                  e.target.value = ''
                }}
              />
              <input
                ref={folderInputRef}
                type="file"
                multiple
                tabIndex={-1}
                onChange={(e) => {
                  const picked = Array.from(e.target.files || [])
                  addStagedFiles(
                    picked.map((file) => ({
                      file,
                      relativePath: normalizeUploadRelativePath(
                        (file as File & { webkitRelativePath?: string }).webkitRelativePath,
                      ),
                    })),
                  )
                  e.target.value = ''
                }}
                {...({ webkitdirectory: '', directory: '' } as React.InputHTMLAttributes<HTMLInputElement>)}
              />
            </div>
            <div className="kg-upload-picker">
              <Button icon={<UploadOutlined />} onClick={() => fileInputRef.current?.click()}>
                选择文件
              </Button>
              <Button icon={<FolderOpenOutlined />} onClick={() => folderInputRef.current?.click()}>
                选择文件夹
              </Button>
              {fileList.length > 0 && (
                <Button type="link" size="small" onClick={() => setFileList([])}>
                  清空列表
                </Button>
              )}
            </div>
            {fileList.length > 0 && (
              <div className="kg-upload-file-panel">
                <div className="kg-upload-file-panel-head">
                  <span>
                    已选 <strong>{fileList.length}</strong> 个文件
                  </span>
                  <span className="kg-upload-file-panel-meta">
                    合计 {formatFileSize(fileList.reduce((s, f) => s + f.file.size, 0))}
                  </span>
                </div>
                <ul className="kg-upload-file-list">
                  {fileList.map((item) => {
                    const displayName = item.relativePath || item.file.name
                    return (
                      <li key={fileTaskId(item.file, item.relativePath)} className="kg-upload-file-row">
                        {item.relativePath ? (
                          <FolderOpenOutlined className="kg-upload-file-icon" />
                        ) : (
                          uploadFileIcon(item.file.name)
                        )}
                        <div className="kg-upload-file-main">
                          <Typography.Text
                            className="kg-upload-file-name"
                            ellipsis={{ tooltip: displayName }}
                          >
                            {displayName}
                          </Typography.Text>
                          <span className="kg-upload-file-size">{formatFileSize(item.file.size)}</span>
                        </div>
                        <Button
                          type="text"
                          size="small"
                          className="kg-upload-file-remove"
                          icon={<DeleteOutlined />}
                          aria-label="移除"
                          onClick={() =>
                            setFileList((prev) =>
                              prev.filter(
                                (x) => fileTaskId(x.file, x.relativePath) !== fileTaskId(item.file, item.relativePath),
                              ),
                            )
                          }
                        />
                      </li>
                    )
                  })}
                </ul>
              </div>
            )}
          </Form.Item>
          <Form.Item name="chunk_strategy" label="切片方式">
            <Select options={strategyOptions} />
          </Form.Item>
          <Form.Item
            name="entity_type"
            label="实体类型"
            extra="用于知识图谱分类，如：制度、流程、人事"
          >
            <Input placeholder="选填，如：制度" />
          </Form.Item>
          <Form.Item name="category" label="文档类型">
            <KnowledgeCategoryBrowseField scope={knowledgeScope} />
          </Form.Item>
          <Form.Item name="tags" label="标签">
            <KnowledgeTagsBrowseField scope={knowledgeScope} placeholder="请选择标签" />
          </Form.Item>
          <Form.Item
            name="use_ocr"
            label="PDF/图片 OCR"
            valuePropName="checked"
            extra={
              ocrEnabled
                ? `使用后台配置的 OCR 厂商${ocrProviderLabel ? `（${ocrProviderLabel}）` : ''}；仅对 PDF 与图片生效。`
                : '后台未启用 OCR，请至「OCR 配置」开启后再使用。'
            }
          >
            <Switch disabled={!ocrEnabled} />
          </Form.Item>
        </Form>
      </Modal>
      <Modal
        title="加入数据集"
        open={datasetModalOpen}
        onCancel={() => setDatasetModalOpen(false)}
        onOk={batchAddToDataset}
      >
        <Select
          style={{ width: '100%' }}
          placeholder="选择数据集"
          value={datasetPickId}
          options={datasetOptions}
          onChange={setDatasetPickId}
        />
      </Modal>
    </div>
  )
}
